import { ChallengeService } from '@duck/api-definitions'
import { MarkdownEditor } from '@duck/ui'
import { useTar } from '@/modules'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import { RelativeDateTime } from '../../datetime'
import { AllowedWeaponsPills, resolveUsedWeapon } from '@components/AllowedWeaponsPills'

export interface FeedbackContentProps {
  challenge: ChallengeService.ChallengeInfo
  /** When true, show employer feedback/grade instead of AI feedback. */
  showEmployer?: boolean
  /** When true (default), render the Challenge Details summary. The admin detail page sets this false to avoid duplicating its cards. */
  showChallengeDetails?: boolean
  /** When true (default), render the score badge. The results dialog sets this false because the score lives in its rail. */
  showGrade?: boolean
}

export const FeedbackContent = ({ challenge, showEmployer = false, showChallengeDetails = true, showGrade = true }: FeedbackContentProps) => {
  const { t } = useTar()
  const { feedbackText, feedbackGrade, employerFeedback } = challenge.challenge
  const displayFeedback = showEmployer ? employerFeedback?.feedback : feedbackText
  const displayGrade = showEmployer ? employerFeedback?.grade : feedbackGrade
  const scoreLabelKey = showEmployer ? 'challenges.employerScore' : 'challenges.yourScore'
  const feedbackLabelKey = showEmployer ? 'challenges.employerFeedback' : 'challenges.aiFeedback'
  const { task } = challenge

  return (
    <>
      {/* Grade Section */}
      {showGrade && displayGrade !== undefined && displayGrade > 5 && (
        <div className="flex justify-center w-full">
          <div className="bg-accent/5 rounded-lg px-8 py-8 border border-accent/40 w-full max-w-md">
            <div className="flex justify-center flex-col">
              <div className="flex items-center justify-center mb-3 w-full">
                <h3 className="text-lg font-semibold text-foreground">
                  {t(scoreLabelKey)}
                </h3>
              </div>
              <div className="flex items-center justify-center w-full">
                <GradeBadge grade={displayGrade} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Text Section */}
      {displayFeedback != null && displayFeedback !== '' && (
        <div className="">
          <h3 className="text-lg font-semibold text-foreground mb-3">
            {t(feedbackLabelKey)}
          </h3>
          <MarkdownEditor
            defaultValue={displayFeedback}
            className="w-full h-auto overflow-y-auto custom-scrollbar"
            mode="view"
          />
        </div>
      )}

      {/* Challenge Details */}
      {showChallengeDetails && (
      <div className="bg-muted/5 rounded-lg p-4 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-3">
          {t('challenges.challengeDetails')}
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center">
            <span className="text-muted-foreground">
              {t('share.publicPage.solvedWith')}
            </span>
            <AllowedWeaponsPills
              className="ms-2"
              allowedWeapons={task.allowedWeapons}
              chosenWeapon={resolveUsedWeapon(
                challenge.challenge.workState?.chosenWeapon,
                task.allowedWeapons
              )}
            />
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('challenges.difficulty')}:
            </span>
            <span className="ms-2 font-medium text-foreground">
              {task.difficulty}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('common.workTime')}:
            </span>
            <span className="ms-2 font-medium text-foreground">
              {t('times.xMinutes', { x: challenge.challenge.workTime })}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('challenges.completed')}:
            </span>
            <span className="ms-2 font-medium text-foreground">
              <RelativeDateTime
                isoDateString={challenge.challenge.endTime}
                fallbackText={t('times.invalidDate')}
              />
            </span>
          </div>
        </div>
      </div>
      )}
    </>
  )
}
